import { useRef, useState } from "react";
import {
  deepEqual,
  getAllFieldValues,
  getCurrentFieldValue,
  handleValidationResult,
  hasValidationErrors,
  isEmpty,
  validateField,
} from "@miplatform/utils";

/**
 * A custom hook for managing form edits, validations, and state management, supporting nested objects.
 *
 * @param {Object} initial - The initial values of the form fields.
 * @param {Array|null} fieldset - An optional array of field definitions, each containing a name and a validation rule.
 * @param {string|null} field - The name of the field to retrieve; if null, retrieves all field values.
 * @param {Object} values - An object containing the new values to update the edits.
 * @param {...string} properties - The field names to check for filled values.
 * @returns {any} edit An object containing various methods and state related to form editing.
 * @returns {Function} edit.reset - Resets the edits to an empty state and clears errors.
 * @returns {Function} edit.update - Updates the current edits with new values and validates them.
 * @returns {Function} edit.getValue - Retrieves the value of a specific field or all fields.
 * @returns {Function} edit.allFilled - Checks if all specified fields are filled.
 * @returns {Function} edit.isDirty - Checks if the current edits differ from the initial values.
 * @returns {Object|null} edit.errors - The current validation errors, or null if there are none.
 * @returns {Function} edit.isValid - A function to check if the current value is valid or not.
 * @returns {Function} edit.validateAll - Validates all current field values and updates errors state.
 * @returns {Function} edit.handleSubmit - Prepares the current edits for submission, optionally adding metadata.
 * @returns {Function} edit.setError - Sets a specific error message for a field.
 * @returns {Function} edit.setValidation - Sets the validation rules for the fields.
 * @returns {Object} edit.getFlatValues - Retrieves the flat values of all fields.
 *
 * @example
 * const initialData = {
 *   user: {
 *     name: "",
 *     description: "",
 *   },
 * };
 * const userFields = [
 *   {
 *     name: "user.name",
 *     validationRule: z
 *       .string()
 *       .min(2, "Title name is too short!")
 *       .max(255, "Title name is too long!"),
 *   },
 *   {
 *     name: "user.description",
 *     validationRule: z.string().optional(),
 *   },
 * ];
 *
 * const edit = useEdit(initialData, userFields);
 *
 *
 * For more information on how to use this hook, see the [MiPlatform documentation](https://dev.azure.com/miviewis/MiPlatform/_wiki/wikis/MiPlatform.wiki/1842/useEdit-hook-Documentation).
 */

export const useEdit = (
  initial,
  fieldset = null,
  config = { getAllErrors: false }
) => {
  const [edits, setEdits] = useState({});
  const [errors, setErrors] = useState(null);
  const validationRef = useRef(null);

  validationRef.current = fieldset?.reduce(
    (acc, field) => ({ ...acc, [field.name]: field.validationRule }),
    {}
  );

  const reset = (resetState = {}) => {
    setEdits(resetState);
    setErrors(null);
  };

  const validate = (values) => {
    const validateResult = {};
    const currentErrors = errors || {};

    Object.entries(values).forEach(([key, value]) => {
      if (!validationRef.current || !validationRef.current[key]) return;

      const { success, error } = validateField(
        value,
        validationRef.current[key]
      );
      handleValidationResult(
        key,
        success,
        error,
        validateResult,
        currentErrors,
        config.getAllErrors
      );
    });

    const hasErrors = hasValidationErrors(currentErrors, validateResult);
    return hasErrors ? { ...currentErrors, ...validateResult } : null;
  };

  const update = (values) => {
    const nestedEdits = JSON.parse(JSON.stringify(edits));

    Object.entries(values).forEach(([key, value]) => {
      const keys = key.split(".");

      const editValue = keys.slice(0, -1).reduce((acc, k) => {
        acc[k] = acc[k] || {};
        return acc[k];
      }, nestedEdits);

      editValue[keys[keys.length - 1]] = value;
      validateAll({ [key]: value });
    });

    setEdits(nestedEdits);
  };

  const allFilled = (...properties) =>
    properties.every((e) => !isEmpty(getValue(e)));

  const isDirty = () => {
    return !deepEqual(initial, getValue());
  };

  const getValue = (field = null) => {
    if (field) {
      return getCurrentFieldValue(initial, edits, field);
    }
    return getAllFieldValues(initial, edits);
  };

  const isValid = () => {
    return validate(getValue()) === null;
  };

  const getFlatValues = () => {
    const allValues = fieldset.reduce((acc, item) => {
      acc[item.name] = getValue(item.name);
      return acc;
    }, {});

    return allValues;
  };

  const validateAll = (value) => {
    const values = value || getFlatValues();

    const results = validate(values);

    setErrors(results);
  };

  const setError = (value = {}, removeFields = []) => {
    const removeList = Array.isArray(removeFields)
      ? removeFields
      : [removeFields];

    setErrors((prev) => {
      const newErrors = { ...prev, ...value };

      removeList.forEach((field) => {
        delete newErrors[field];
      });

      return newErrors;
    });
  };

  const deleteError = (field) => {
    const fields = Array.isArray(field) ? field : [field];

    setErrors((prev) => {
      const newErrors = { ...prev };
      fields.forEach((f) => {
        if (newErrors[f]) {
          delete newErrors[f];
        }
      });
      return newErrors;
    });
  };

  const handleSubmit = () => {
    return { ...edits, modifiedOn: initial?.modifiedOn };
  };

  const setValidation = (fields) => {
    validationRef.current = fields.reduce(
      (acc, field) => ({ ...acc, [field.name]: field.validationRule }),
      {}
    );
  };

  return {
    reset,
    update,
    getValue,
    allFilled,
    edits,
    isDirty,
    errors,
    setError,
    deleteError,
    isValid,
    validateAll,
    getFlatValues,
    handleSubmit,
    setValidation,
  };
};
